import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Page } from '../../../layout';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import ResponsiveUrlTabMenu from '@shared/components/ResponsiveUrlTabMenu';
import { ALL_APPRAISER_TABS } from '../shared/constants/appraiserTabs';
import { useQuery } from '@apollo/client';
import GET_APPRAISER_OBSERVATION_REPORTS from '../../../userManagement/queries/getAppraiserObservationReportsQuery';
import ApolloError from '@shared/components/ApolloError';
import { useObservationReportFileDownload } from '../../../shared/hooks/useObservationReportFileDownload';
import AppraisserObservationReportsTable from './AppraisserObservationReportsTable';
import AppraisserObservationReportMapper from '../../mappers/AppraisserObservationReportMapper';
import PageInstructions from '@shared/components/PageInstructions';

const PAGE_TITLE = 'Certification Pathway Progress';
const PAGE_DESCRIPTION =
  'The below tables contain the observation results.  Results are in reverse chronological order.' +
  ' Selecting download provides you a copy that was submitted';
const ATM_TABLE_TITLE = 'ATM Observation/Apprenticeship';
const ATL_TABLE_TITLE = 'ATL Observation/Apprenticeship';

const CertificationPathwayProgressPage = () => {
  const location = useLocation();

  const { userId, orgId } = location.state ? location.state : {};

  const breadcrumbPathItems = [
    {
      name: 'User Management',
      url: `/user-management/${orgId}`
    },
    { name: PAGE_TITLE, url: '' }
  ];

  const { handleFileDownload } = useObservationReportFileDownload();

  const {
    data: observationReportsData,
    loading: observationReportsLoading,
    error: observationReportsError
  } = useQuery(GET_APPRAISER_OBSERVATION_REPORTS, {
    variables: {
      externalUserId: parseInt(userId)
    },
    skip: !userId
  });

  const reportsResponse = observationReportsData?.appraiserObservationReports;
  const atmReports = AppraisserObservationReportMapper.toTableData(
    reportsResponse?.AtmReports || []
  );
  const atlReports = AppraisserObservationReportMapper.toTableData(
    reportsResponse?.AtlReports || []
  );

  if (!location.state || !userId || !orgId) {
    return <Navigate to='/user-management/organization-search' />;
  }

  if (observationReportsError) {
    return <ApolloError />;
  }

  return (
    <Page title={PAGE_TITLE}>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <div className='user-management-tabs'>
        <ResponsiveUrlTabMenu
          tabs={ALL_APPRAISER_TABS}
          urlParams={{
            orgId: orgId,
            userId: userId
          }}
        />
      </div>
      <div className='certification-pathway-progress-page'>
        <PageInstructions title={PAGE_TITLE} description={PAGE_DESCRIPTION} />

        {(atmReports.length > 0 || observationReportsLoading) && (
          <AppraisserObservationReportsTable
            loading={observationReportsLoading}
            title={ATM_TABLE_TITLE}
            data={atmReports}
            onDownloadFile={handleFileDownload}
          />
        )}
        {(atlReports.length > 0 || observationReportsLoading) && (
          <AppraisserObservationReportsTable
            loading={observationReportsLoading}
            title={ATL_TABLE_TITLE}
            data={atlReports}
            onDownloadFile={handleFileDownload}
          />
        )}
      </div>
    </Page>
  );
};

export default CertificationPathwayProgressPage;
